import { TextAreaField } from '@procureai/ui-form-mui/fields';
import { SendHorizontal } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { InputGroupButton } from '@/shared/components/ui/input-group';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Separator } from '@/shared/components/ui/separator';
import { Spinner } from '@/shared/components/ui/spinner';

import { CommentsItem, type Comment } from './components/CommentsItem/CommentsItem';

interface CommentsProps {
  className?: string;
  comments?: Comment[];
  disabled?: boolean;
  loading?: boolean;
  sending?: boolean;
  onAddComment: (text: string) => void;
  onEditComment: (id: string, newText: string) => void;
  onDeleteComment: (id: string) => void;
}

export const Comments = ({
  className,
  comments = [],
  disabled = false,
  loading = false,
  sending = false,
  onAddComment,
  onEditComment,
  onDeleteComment,
}: CommentsProps) => {
  const { t: tComments } = useTranslation(undefined, { keyPrefix: 'Comments' });
  const [newComment, setNewComment] = useState<Union.Nullable<string>>('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [comments]);

  const handleAddComment = () => {
    const trimmedComment = newComment?.trim();
    if (trimmedComment) {
      onAddComment(trimmedComment);
      setNewComment('');
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-3 p-4" onKeyUp={handleKeyUp}>
        <TextAreaField
          placeholder={tComments('writeComment')}
          value={newComment}
          onChange={setNewComment}
          disabled={disabled}
          endAddon={
            <InputGroupButton
              onClick={handleAddComment}
              disabled={disabled || sending}
              variant="default"
              className="ml-auto"
            >
              <SendHorizontal />
            </InputGroupButton>
          }
        />
      </div>

      <Separator />

      <ScrollArea className={'[&>[data-slot=scroll-area-viewport]]:max-h-[500px] mr-2'}>
        <div className="flex flex-col px-4">
          {loading && <Spinner className="mx-auto my-8 size-8" />}
          {comments.map((comment, idx) => (
            <>
              {idx > 0 && <Separator key={`sep-${comment.id}`} />}
              <CommentsItem key={comment.id} comment={comment} onEdit={onEditComment} onDelete={onDeleteComment} />
            </>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
